import React, { createContext, useContext, ReactNode, useState, useMemo, useEffect } from 'react';

type GameState = 'playing' | 'won' | 'lost';

// Global state system using pub/sub pattern - allows components outside the provider tree
// (like ConditionalFooter in the layout) to react to game state changes
let globalGameState: GameState = 'playing';
const gameStateListeners = new Set<(state: GameState) => void>();

// Updates global state and notifies all subscribers
export const setGlobalGameState = (state: GameState) => {
  globalGameState = state;
  gameStateListeners.forEach(listener => listener(state));
};

// Hook for components outside the provider to subscribe to game state changes
export const useGlobalGameState = () => {
  const [gameState, setGameState] = useState<GameState>(globalGameState);

  useEffect(() => {
    const listener = (state: GameState) => setGameState(state);
    gameStateListeners.add(listener);
    return () => {
      gameStateListeners.delete(listener);
    };
  }, []);

  return gameState;
};

// --------------------

interface CoverStoryContextType {
  gridSize: string;
  allAlbums: CoverStoryAlbum[];
  gameState: GameState;
  setGameState: (state: GameState) => void;
}

interface CoverStoryProviderProps {
  children: ReactNode;
  options: CoverStoryOptions;
}

const CoverStoryContext = createContext<CoverStoryContextType | undefined>(undefined);

// --------------------

// Provider wraps Cover Story game components with shared state and CMS data
export const CoverStoryProvider: React.FC<CoverStoryProviderProps> = ({ children, options }) => {
  const [gridSize] = useState(options?.coverStoryGridSize?.[0]);
  const [allAlbums, setAllAlbums] = useState<CoverStoryAlbum[]>([]);
  const [gameState, setGameStateLocal] = useState<GameState>('playing');

  // Syncs context state with global state for cross-tree communication
  const setGameState = (state: GameState) => {
    setGameStateLocal(state);
    setGlobalGameState(state);
  };

  // Fetch all cover story albums from API endpoint
  const fetchAllAlbums = async () => {
    try {
      const response = await fetch('/api/cover-story/albums/');
      const data = await response.json();
      setAllAlbums(data.albums);
    } catch (error) {
      console.error('Error fetching cover story albums:', error);
    }
  }

  // Fetch albums on mount
  useEffect(() => {
    fetchAllAlbums();
  }, []);

  // Memoize context value to prevent unnecessary re-renders
  const value: CoverStoryContextType = useMemo(() => ({
    gridSize,
    allAlbums,
    gameState,
    setGameState,
  }), [gridSize, allAlbums, gameState]);

  // Render
  return (
    <CoverStoryContext.Provider value={value}>
      {children}
    </CoverStoryContext.Provider>
  );
};

// Hook for components inside the provider to access Cover Story data and state
export const useCoverStory = () => {
  const context = useContext(CoverStoryContext);
  return context || {
    gridSize: '',
    allAlbums: [] as CoverStoryAlbum[],
    gameState: 'playing' as GameState,
    setGameState: () => {},
  };
};
